/* eslint-disable no-restricted-syntax */
import router from '@/router'
import { isMarkupFeature } from '@/lib/feature-identification'
import type MapFeature from '@/lib/map-feature'

import markupService from '@/services/markup-service'

interface BulkSelectMap {
  boxZoom: { disable(): void; enable(): void }
  dragPan: { disable(): void; enable(): void }
  getCanvas(): HTMLCanvasElement
  queryRenderedFeatures(
    bbox: [[number, number], [number, number]]
  ): MapFeature[]
}

interface BulkSelectEvent {
  originalEvent: MouseEvent
}

let _map: BulkSelectMap
let selectStart: [number, number]
let selectCurrent: [number, number]
let selectBox: HTMLDivElement | null

/**
 * *(AI-generated)*
 * Initializes the bulk selection box interaction on the map.
 */
export const initMapBulkSelect = (
  map: BulkSelectMap,
  event: BulkSelectEvent
): void => {
  _map = map

  _map.boxZoom.disable()
  _map.dragPan.disable()

  // Call functions for the following events
  document.addEventListener('mousemove', _onSelectMouseMove)
  document.addEventListener('mouseup', _onSelectMouseUp)

  // Capture the first xy coordinates
  selectStart = _mousePos(map, event.originalEvent)
}

// Return the xy coordinates of the mouse position
const _mousePos = (map: BulkSelectMap, e: MouseEvent): [number, number] => {
  const canvas = map.getCanvas()
  const rect = canvas.getBoundingClientRect()

  // Return [x, y] tuple for Mapbox PointLike type
  return [
    e.clientX - rect.left - canvas.clientLeft,
    e.clientY - rect.top - canvas.clientTop,
  ]
}

const _onSelectMouseMove = (e: MouseEvent): void => {
  markupService.clearHighlightedMarkups()
  markupService.clearFocusedMarkup()

  const canvas = _map.getCanvas()
  // Capture the ongoing xy coordinates
  selectCurrent = _mousePos(_map, e)

  // Append the box element if it doesnt exist
  if (!selectBox) {
    selectBox = document.createElement('div')
    selectBox.classList.add('bulk-select-box')
    canvas.parentNode?.appendChild(selectBox)
  }

  const selectStartX = selectStart[0]
  const selectCurrentX = selectCurrent[0]
  const selectStartY = selectStart[1]
  const selectCurrentY = selectCurrent[1]
  // selectStart and selectCurrent are [x, y] tuples
  const minX = Math.min(selectStartX, selectCurrentX),
    maxX = Math.max(selectStartX, selectCurrentX),
    minY = Math.min(selectStartY, selectCurrentY),
    maxY = Math.max(selectStartY, selectCurrentY)

  // Adjust width and xy position of the box element ongoing
  const pos = `translate(${minX}px, ${minY}px)`
  selectBox.style.transform = pos
  selectBox.style.width = maxX - minX + 'px'
  selectBox.style.height = maxY - minY + 'px'
}

const _onSelectMouseUp = (e: MouseEvent): void => {
  // Capture xy coordinates
  _finishSelect([selectStart, _mousePos(_map, e)])
}

const _finishSelect = (bbox: [[number, number], [number, number]]): void => {
  // Remove these events now that finish has been called.
  document.removeEventListener('mousemove', _onSelectMouseMove)
  document.removeEventListener('mouseup', _onSelectMouseUp)

  // remove select box html
  if (selectBox) {
    selectBox.parentNode?.removeChild(selectBox)
    selectBox = null
  }

  // If bbox exists use this value as the argument for `queryRenderedFeatures`
  if (bbox) {
    const features = _map.queryRenderedFeatures(bbox)

    // get markups within select bbox and highlight
    const selectedMarkups = features.filter((f) => {
      if (isMarkupFeature(f)) {
        markupService.addMarkupHighlight(String(f.id))
        return f
      }
      return null
    })

    if (selectedMarkups.length) {
      // open select content card with markups pre-selected
      const selectRoute = {
        path: `/fish/map/content/all/select/`,
        state: { clear: false },
      }
      const routeName = router.currentRoute.value.name
      if (
        typeof routeName === 'string' &&
        routeName.startsWith('content-select-unspecified-collection')
      ) {
        router.replace(selectRoute).catch(() => {})
      } else {
        router.push(selectRoute).catch(() => {})
      }
    }
  }

  _map.boxZoom.enable()
  _map.dragPan.enable()
}
